import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, IconButton, SystemIcon, TextField, Typography } from '@volvo/vcdk-react';
import { FileUploader } from '@volvo/vcdk-react/FileUploader';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ICreateUnitMetadata, IUpdateUnitMetadata } from '@/service/vpc/types/ICreateUnitMetadata';
import type { IHomePort, IUnit } from '@/service/vpc/types/IUnit';
import type { FileUploaderEvent } from '@volvo/vcdk/file-uploader';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import GeoComboField from '@/components/GeoComboField';
import UserMetadataAvatar from '@/components/UserMetadataAvatar';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { queryKeys, unitByIdQueryOptions } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';

type Props = {
  endCustomerUnitId: string;
};

const UnitSettings: React.FC<Props> = ({ endCustomerUnitId }) => {
  const { t } = useTranslation();
  const { fleetAccountId, userHasRole } = useFleetAccount();
  const canEdit = userHasRole(['Owner', 'Manager'], fleetAccountId!);
  const queryClient = useQueryClient();

  const { data: unit } = useQuery(unitByIdQueryOptions({ endCustomerUnitId: endCustomerUnitId! }));

  const [isEditMode, setIsEditMode] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [pendingAvatarDelete, setPendingAvatarDelete] = useState(false);
  const [unitName, setUnitName] = useState<string>(unit?.metadata?.unitName || unit?.unitName || '');
  const [homePort, setHomePort] = useState<IHomePort | null>(unit?.metadata?.homePort ?? null);

  const { mutateAsync: addUnitsMetadata, isPending } = useMutation({
    mutationFn: async ({ metadata, image }: { metadata: ICreateUnitMetadata; image?: File }) =>
      vpcService.Units.addUnitsMetadata({ metadata, image }),
  });

  const { mutateAsync: updateUnitsMetadata } = useMutation({
    mutationFn: async ({
      fleetAccountId,
      endCustomerUnitId,
      metadata,
      image,
    }: {
      fleetAccountId: string;
      endCustomerUnitId: string;
      metadata: IUpdateUnitMetadata;
      image?: File;
    }) => vpcService.Units.updateUnitsMetadata({ fleetAccountId, endCustomerUnitId, metadata, image }),
  });

  const { mutateAsync: deleteAvatar } = useMutation({
    mutationFn: async () =>
      vpcService.Units.deleteAvatar({
        fleetAccountId: fleetAccountId!,
        endCustomerUnitId,
      }),
  });

  const handleFilesSelected = (event: FileUploaderEvent) => {
    setImageFile(event.detail.files[0].file);
  };

  const handleFilesRemoved = () => {
    setImageFile(null);
  };

  const imagePreviewUrl = useMemo(() => {
    if (!imageFile) return null;
    return URL.createObjectURL(imageFile);
  }, [imageFile]);

  const getPreviewUrl = () => {
    if (pendingAvatarDelete) return null;
    return imagePreviewUrl ?? unit?.metadata?.blobUrl ?? null;
  };

  const previewUrl = getPreviewUrl();

  // Clean up generated object URLs when file selection changes or component unmounts.
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const handleCancel = () => {
    setPendingAvatarDelete(false);
    setImageFile(null);
    setUnitName(unit?.metadata?.unitName || unit?.unitName || '');
    setHomePort(unit?.metadata?.homePort ?? null);
    setIsEditMode(false);
  };

  const updateUnitMetadataInCaches = (updater: (prev: IUnit['metadata']) => IUnit['metadata']) => {
    queryClient.setQueryData([queryKeys.getUnitById, endCustomerUnitId], (prev: IUnit | undefined) => {
      if (!prev) return prev;
      return { ...prev, metadata: updater(prev.metadata) };
    });

    queryClient.setQueryData([queryKeys.getUnitsWithFBB], (prev: IUnit[] | undefined) => {
      if (!prev) return prev;
      return prev.map((u) =>
        String(u.endCustomerUnitId) === endCustomerUnitId ? { ...u, metadata: updater(u.metadata) } : u
      );
    });
  };

  const handleOnSubmit = async () => {
    if (!fleetAccountId) return;

    if (pendingAvatarDelete && !imageFile) {
      await deleteAvatar();
      setPendingAvatarDelete(false);
      updateUnitMetadataInCaches((prev) => (prev ? { ...prev, blobUrl: '', blobFileName: '' } : prev));
    }

    // Build the metadata object, only including fields that have values
    const metadata: ICreateUnitMetadata | IUpdateUnitMetadata = {};
    if (unitName) {
      metadata.unitName = unitName;
    }
    if (homePort) {
      metadata.homePort = homePort;
    }

    let response;
    if (unit?.metadata) {
      response = await updateUnitsMetadata({
        fleetAccountId: fleetAccountId,
        endCustomerUnitId: endCustomerUnitId,
        metadata: metadata,
        ...(!!imageFile && { image: imageFile }),
      });
    } else {
      response = await addUnitsMetadata({
        metadata: {
          endCustomerUnitId: endCustomerUnitId,
          fleetAccountId: fleetAccountId,
          ...metadata,
        },
        ...(!!imageFile && { image: imageFile }),
      });
    }

    if (response?.value) {
      updateUnitMetadataInCaches((prev) => (prev ? { ...prev, ...response.value } : prev));
    }

    setIsEditMode(false);
  };

  return (
    <Card>
      <CardContent>
        <div className="flex flex-row justify-between">
          <Typography variant="heading4" className="mb-6">
            {t(($) => $.pages.unit.settings.unit_settings.title)}
          </Typography>
          {canEdit && <IconButton icon="edit" size="medium" onClick={() => setIsEditMode(!isEditMode)} />}
        </div>
        <div className="flex flex-col gap-8 md:flex-row">
          <UserMetadataAvatar url={previewUrl} showPlaceholder />
          <div className="grid flex-1 grid-cols-1 gap-8 md:grid-cols-2">
            {isEditMode && (
              <div className="col-span-full flex flex-col gap-2">
                {(unit?.metadata?.blobFileName || unit?.metadata?.blobUrl) && !pendingAvatarDelete ? (
                  <>
                    <Typography variant="caption2">
                      {t(($) => $.pages.unit.settings.unit_settings.upload_image)}
                    </Typography>
                    <Typography variant="caption2" className="text-subtle">
                      {t(($) => $.pages.unit.settings.unit_settings.helperText)}
                    </Typography>
                    <div className="border-subtle mt-1 flex items-center gap-3 rounded border px-4 py-3">
                      <SystemIcon icon="checkmark-circle" className="[&::part(svg)]:text-success shrink-0" />
                      <Typography variant="caption1" className="text-subtle flex-1 truncate">
                        {unit?.metadata?.blobFileName || unit?.metadata?.blobUrl?.split('/').pop() || ''}
                      </Typography>
                      <IconButton icon="trash" size="small" onClick={() => setPendingAvatarDelete(true)} />
                    </div>
                  </>
                ) : (
                  <FileUploader
                    allowedTypes="image/*"
                    maxFiles={1}
                    helperText={t(($) => $.pages.unit.settings.unit_settings.helperText)}
                    label={t(($) => $.pages.unit.settings.unit_settings.upload_image)}
                    variant="button"
                    maxFileSize={3145728} // 3 MB
                    multiple={false}
                    onFilesSelected={handleFilesSelected}
                    onFilesRemoved={handleFilesRemoved}
                  >
                    <SystemIcon icon="upload-cloud" size={20} />
                    {t(($) => $.pages.unit.settings.unit_settings.add_files)}
                  </FileUploader>
                )}
              </div>
            )}
            <TextField
              disabled={!isEditMode}
              label={t(($) => $.pages.unit.settings.unit_settings.unit_name)}
              value={unitName}
              maxLength={50}
              onInput={(e) => {
                setUnitName(e.detail.value);
              }}
            />
            <GeoComboField
              label={
                unit?.mainSegment === 'Marine'
                  ? t(($) => $.pages.unit.settings.unit_settings.home_port)
                  : t(($) => $.pages.unit.settings.unit_settings.operating_location)
              }
              defaultValue={homePort?.address || ''}
              disabled={!isEditMode}
              setLocation={(location) => {
                setHomePort({
                  address: location.homePort || '',
                  latitude: location.latitude ?? 0,
                  longitude: location.longitude ?? 0,
                });
              }}
              countrycode={unit?.bestKnownLocation.country.toLowerCase() ?? 'se'} // Default to Sweden, can be dynamic based on user/fleet location
            />
            {isEditMode && (
              <div className="flex flex-row gap-4">
                <Button variant="secondary" disabled={isPending} onClick={handleCancel}>
                  {t(($) => $.common.cancel)}
                </Button>
                <Button variant="primary" onClick={handleOnSubmit} disabled={isPending} loading={isPending}>
                  {t(($) => $.common.save)}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default withComponentErrorBoundary(UnitSettings);
